import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Alert, Col, Row } from "components/bootstrap";
import { Spinner } from "components/common";
import useTokenUsageBreakdown from "aiagent/hooks/useTokenUsageBreakdown";
import UsagePlateStats from "aiagent/AgentWorkflowUsage/UsagePlateStats";
import AIFeaturesStatusAlerts from "ai-management/components/AIFeaturesStatusAlerts";
import UsageSearchForm from "./UsageSearchForm";
import TokenUsagePieChart from "./TokenUsagePieChart";
import useWorkflowTypeOptions from "./hooks/useWorkflowTypeOptions";
const SEVEN_DAYS_IN_SECONDS = 7 * 24 * 60 * 60;
const NO_SELECTION = [];
const ChartsRow = styled.div(
  ({ theme, $columns }) => css`
    display: grid;
    gap: ${theme.spacings.lg};
    grid-template-columns: repeat(${$columns}, minmax(0, 1fr));

    @media (max-width: ${theme.breakpoints.max.sm}) {
      grid-template-columns: 1fr;
    }
  `
);
const buildDefaultUsageFilters = () => ({
  timerange: { type: "relative", range: SEVEN_DAYS_IN_SECONDS },
  provider: null,
  model: null,
  type: null
});
const chartSlices = (slices, labelFor) => slices.map(({ key, total_tokens }) => ({ value: key, label: labelFor(key), totalTokens: total_tokens }));
const AgentWorkflowUsage = () => {
  var _a;
  const { t } = useTranslation("enterprise_ai");
  const [filters, setFilters] = useState(buildDefaultUsageFilters());
  const { data: workflowTypeOptions } = useWorkflowTypeOptions();
  const { data: breakdown, isLoading, isError } = useTokenUsageBreakdown(filters);
  const workflowTypeLabels = useMemo(
    () => new Map(workflowTypeOptions.map(({ value, label }) => [value, label])),
    [workflowTypeOptions]
  );
  const workflowTypeSlices = useMemo(
    () => {
      var _a2;
      return chartSlices((_a2 = breakdown == null ? void 0 : breakdown.by_type) != null ? _a2 : [], (key) => {
        var _a3;
        return (_a3 = workflowTypeLabels.get(key)) != null ? _a3 : key;
      });
    },
    [breakdown == null ? void 0 : breakdown.by_type, workflowTypeLabels]
  );
  const toggleWorkflowTypeFilter = useCallback(
    (value) => setFilters((previous) => {
      var _a2;
      const current = (_a2 = previous.type) != null ? _a2 : [];
      const next = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];
      return { ...previous, type: next.length ? next : null };
    }),
    []
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(AIFeaturesStatusAlerts, null), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(UsageSearchForm, { filters, onApply: setFilters }))), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, isLoading && /* @__PURE__ */ React.createElement(Spinner, { text: t("agent_workflow_usage.loading") }), isError && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: t("agent_workflow_usage.load_failed_title") }, t("agent_workflow_usage.load_failed")), !isLoading && !isError && breakdown && /* @__PURE__ */ React.createElement(ChartsRow, { $columns: 2 }, /* @__PURE__ */ React.createElement(UsagePlateStats, { summary: breakdown.total }), /* @__PURE__ */ React.createElement(
    TokenUsagePieChart,
    {
      title: t("agent_workflow_usage.by_workflow_type"),
      slices: workflowTypeSlices,
      selected: (_a = filters.type) != null ? _a : NO_SELECTION,
      onSelect: toggleWorkflowTypeFilter
    }
  )))));
};
export default AgentWorkflowUsage;
